<script setup lang="ts">
import BrandBlock from '@/components/BrandBlock.vue';
import { Prose } from '@dabble/quillui';

withDefaults(defineProps<{ showLogo?: boolean }>(), { showLogo: true });
</script>

<template>
  <div class="auth-layout">
    <div v-if="showLogo" class="auth-layout__logo-wrapper">
      <img src="/images/dabble-logo-inverted.svg" alt="Dabble" />
    </div>
    <div v-if="$slots.header" class="auth-layout__header">
      <slot name="header" />
    </div>
    <div class="auth-layout__body">
      <div v-if="$slots.aside" class="auth-layout__aside">
        <slot name="aside" />
      </div>
      <div class="auth-layout__main">
        <BrandBlock class="auth-layout__block">
          <slot />
        </BrandBlock>
        <div v-if="$slots.footer" class="auth-layout__footer">
          <Prose variant="secondary" size="small" align="center">
            <slot name="footer" />
          </Prose>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.auth-layout {
  background: url('/images/auth-background.png');
  background-position: center bottom;
  background-size: cover;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  padding: space('medium');
  box-sizing: border-box;
  box-shadow: inset 0 0 100px 10px rgba(#000, 0.3);

  &__logo-wrapper {
    width: 100%;
    height: calc(64px + space('medium') * 2);
    margin-bottom: space('medium');

    img {
      position: relative;
      left: -50px;
      width: 100%;
      height: 100%;
      object-fit: contain;
      filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.2));
    }
  }

  &__header {
    display: flex;
    justify-content: center;
    margin-bottom: space('medium');
  }

  &__body {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: center;
    gap: space('large');
  }

  &__main {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: min(100%, 16rem + space('huge') * 2);
  }

  &__aside {
    padding-bottom: space('xlarge') + space('tiny');
  }

  &__footer {
    // Anchored below the card without adding to the card's height, so a sibling
    // column can bottom-align to the card itself rather than the footer.
    position: relative;
    margin-top: space('small');
    background-color: theme('color-bg-app');
    background-image: linear-gradient(theme('color-bg-info-subtle'), theme('color-bg-info-subtle'));
    border-radius: borderRadius('xxsmall');
    padding: space('xxxsmall') space('small');
    box-shadow: elevation('medium');
    white-space: nowrap;
  }
}
</style>
